import React, { useMemo } from 'react';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';
import { Grid, Paper, Typography } from '@mui/material';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import { useDispatch, useSelector } from 'react-redux';
import { addCarrierSettings, updateCarrierSettings } from '@Reducers/User/actions';
import SbButton from '@Components/Button';
import Divider from '@Components/Divider';
import * as Yup from 'yup';
import { upsCarrier } from './Type';
import { setCarrierSettings } from '@Reducers/User';
import {
  UpsPayorOptions,
  UpsServiceCodeOptions,
  UpsPackagingCodeOptions,
  UpsReasonForExportOptions,
} from '../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/components/UPS/constants';

interface propsTypes {
  onClose: () => void;
  isEditable: boolean;
}

const upsValidationSchema = Yup.object().shape({
  accountNo: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Account value is required'),
        display: Yup.string().required('Account display is required'),
      }),
    )
    .min(1, 'At least one account number is required')
    .required('Account number is required')
    .default([]),

  serviceCode: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Service value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Service is required')
    .required('Service is required')
    .default([]),

  packagingCode: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Packaging value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Packaging is required')
    .required('Packaging is required')
    .default([]),

  payor: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Payor value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Payor is required')
    .required('Payor is required')
    .default([]),

  ReasonforExport: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Reason for Export value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Reason for Export is required')
    .required('Reason for Export is required')
    .default([]),
});

const Ups = (props: propsTypes) => {
  const { onClose, isEditable } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { vendersAccount } = useSelector((state: RootStateType) => state.adminShipment);
  const { userDataById, carrierSetting } = useSelector((state: RootStateType) => state.user);

  console.info(carrierSetting);

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })),
    [vendersAccount],
  );

  const { values, setFieldValue, errors, handleSubmit } = useFormik<upsCarrier>({
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: upsValidationSchema,
    initialValues: {
      ...carrierSetting.ups,
    },
    onSubmit(values) {
      const finalPayload = {
        ...carrierSetting.ups,
        ...values,
      };

      if (userDataById?._id) {
        if (carrierSetting.ups._id) {
          dispatch(updateCarrierSettings(finalPayload));
        } else {
          dispatch(addCarrierSettings(finalPayload));
        }
      } else {
        dispatch(
          setCarrierSettings({
            values: finalPayload,
            carrierType: 'ups',
          }),
        );
      }

      onClose();
    },
  });

  return (
    <div>
      <Paper
        variant="outlined"
        sx={{
          my: '2rem',
          p: '2rem',
        }}
      >
        <Grid container item xs={12}>
          <Grid container item mb={3}>
            <Grid item xs={12} md={6}>
              <Typography className="font-medium l-text pb-3">UPS Information</Typography>
            </Grid>
          </Grid>
          <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
            <Grid container item columnSpacing={10} rowSpacing={2}>
              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="accountNo"
                  label="UPS Account"
                  required
                  multiple={true}
                  placeholder="Select"
                  name="accountNo"
                  value={values?.accountNo?.map(item => item.value)}
                  options={accountOptions}
                  onChange={(_, selectedObj) => {
                    setFieldValue('accountNo', selectedObj);
                  }}
                  error={Array.isArray(errors.accountNo) ? errors.accountNo.join(', ') : errors.accountNo}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="serviceCode"
                  label="Service"
                  required
                  multiple={true}
                  placeholder="Select"
                  name="serviceCode"
                  value={values?.serviceCode?.map(item => item.value)}
                  options={UpsServiceCodeOptions}
                  onChange={(_, selectedObj) => {
                    setFieldValue('serviceCode', selectedObj);
                  }}
                  error={
                    Array.isArray(errors.serviceCode) ? errors.serviceCode.join(', ') : (errors.serviceCode as string)
                  }
                />
              </Grid>
            </Grid>

            <Grid container item columnSpacing={10} rowSpacing={2}>
              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="packagingCode"
                  label="Packaging"
                  required
                  multiple={true}
                  placeholder="Select"
                  name="packagingCode"
                  value={values?.packagingCode?.map(item => item.value)}
                  options={UpsPackagingCodeOptions}
                  onChange={(_, selectedObj) => {
                    setFieldValue('packagingCode', selectedObj);
                  }}
                  error={
                    Array.isArray(errors.packagingCode)
                      ? errors.packagingCode.join(', ')
                      : (errors.packagingCode as string)
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="payor"
                  label="Payor"
                  required
                  multiple={true}
                  placeholder="Select"
                  name="payor"
                  value={values?.payor?.map(item => item.value)}
                  options={UpsPayorOptions}
                  onChange={(_, selectedObj) => {
                    setFieldValue('payor', selectedObj);
                  }}
                  error={Array.isArray(errors.payor) ? errors.payor.join(', ') : (errors.payor as string)}
                />
              </Grid>
            </Grid>

            <Grid container item columnSpacing={10} rowSpacing={2}>
              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="ReasonforExport"
                  label="Reason for Export"
                  required
                  multiple={true}
                  placeholder="Select"
                  name="ReasonforExport"
                  value={values?.ReasonforExport?.map(item => item.value)}
                  options={UpsReasonForExportOptions}
                  onChange={(_, selectedObj) => {
                    setFieldValue('ReasonforExport', selectedObj);
                  }}
                  error={
                    Array.isArray(errors.ReasonforExport)
                      ? errors.ReasonforExport.join(', ')
                      : (errors.ReasonforExport as string)
                  }
                />
              </Grid>
            </Grid>

            <Divider className="mt-3 mb-3" />
            <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
              <Grid container item sx={{ gap: '1rem' }}>
                <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                  <SbButton
                    type="submit"
                    disabled={!isEditable}
                    variant="contained"
                    sx={{ borderRadius: '4px !important' }}
                    className="w-full"
                    onClick={() => {
                      handleSubmit();
                    }}
                  >
                    Save
                  </SbButton>
                </Grid>
                {onClose && (
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      type="button"
                      sx={{ borderRadius: '4px !important' }}
                      className="w-full"
                      variant="outlined"
                      onClick={onClose}
                    >
                      Cancel
                    </SbButton>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

export default Ups;
